import { createHOC } from 'vue-hoc';
import concat from 'lodash/concat';
import difference from 'lodash/difference';
import get from 'lodash/get';
import gql from 'graphql-tag';

import withUser from '~lib/common/hoc/withUser';

import fetchOrgData from '~lib/common/utils/fetchOrgData';
import casesService from '~lib/common/providers/casesService';

import SharingTable from './SharingTable';

const options = {
  name: 'SharingTableContainer',
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      state: {
        loading: false,
        sharing: false,
        orgData: {
          users: [],
          groups: [],
        },
        error: null,
      },
    };
  },
  async created() {
    this.state.loading = true;
    fetchOrgData(this.user).then(
      result => {
        this.state.orgData = result;
      },
      error => {
        console.log(error);
      },
    ).then(() => {
        this.state.loading = false;
    });
  },
  methods: {
    getUpdatedUserShares(caseConf, newAccess) {
      const addedUsers = difference(
        get(newAccess, 'run.users'),
        get(caseConf.access, 'run.users'),
      );

      const removedUsers = difference(
        get(caseConf.access, 'run.users'),
        get(newAccess, 'run.users'),
      );

      let userShares = [];
      if (caseConf.shares) {
        userShares = caseConf.shares.filter(share => share.shareType === 'user');
      }

      // Filter out removed users
      userShares = userShares.filter(share =>
          !removedUsers.includes(share.to) ||
          share.from !== this.user.id);

      // Append added users
      addedUsers.forEach(added => {
        userShares.push({
          from: this.user.id,
          shareType: 'user',
          to: added,
        });
      });

      return userShares;
    },
    getUpdatedGroupShares(caseConf, newAccess) {
      const addedGroups = difference(
        get(newAccess, 'run.groups'),
        get(caseConf.access, 'run.groups'),
      );

      const removedGroups = difference(
        get(caseConf.access, 'run.groups'),
        get(newAccess, 'run.groups'),
      );

      let groupShares = [];
      if (caseConf.shares) {
        groupShares = caseConf.shares.filter(share => share.shareType === 'group');
      }

      // Filter out removed groups
      groupShares = groupShares.filter(share =>
          !removedGroups.includes(share.to) ||
          share.from !== this.user.id);

      // Append added groups
      addedGroups.forEach(added => {
        groupShares.push({
          from: this.user.id,
          shareType: 'group',
          to: added,
        });
      });

      return groupShares;
    },
    getUpdatedShares(caseConf, newAccess) {
      return concat(this.getUpdatedUserShares(caseConf, newAccess),
                    this.getUpdatedGroupShares(caseConf, newAccess));
    },
  },
};

const renderWith = {
  props: {
    loading() {
      return this.state.loading;
    },
    sharing() {
      return this.state.sharing;
    },
    users() {
      return this.state.orgData.users;
    },
    groups() {
      return this.state.orgData.groups;
    },
    error() {
      return this.state.error;
    },
  },
  listeners: {
    async updateAccess(caseConf, updatedAccess) {
      if (!updatedAccess) {
        return;
      }
      try {
        this.state.sharing = true;
        this.state.error = null;
        const updatedShares = this.getUpdatedShares(caseConf, updatedAccess);
        await casesService.mutate({
          mutation: gql`
            mutation upsertCaseConfiguration(
              $caseConfiguration: CaseConfigurationInput
            ) {
              upsertCaseConfiguration(caseConfiguration: $caseConfiguration) {
                id
                name
              }
            }
          `,
          variables: {
            caseConfiguration: {
              id: caseConf.id,
              access: updatedAccess,
              shares: updatedShares,
            },
          },
        });
        this.$emit('update', {
          id: this.caseConf.id,
          shares: updatedShares,
        });
      } catch (e) {
        console.error(e);
        if (e.graphqlErrors && e.graphqlErrors.length) {
          this.state.error = e.graphqlErrors[0];
        }
        this.state.error = e;
      } finally {
        this.state.sharing = false;
      }
    },
  },
};

const SharingTableContainer = createHOC(SharingTable, options, renderWith);
export default withUser()(SharingTableContainer);
